import { useEffect, useState } from 'react';
import { Table, Modal, Form, Card, Switch, Input } from 'antd';
import { FeatureFlags } from '../../../types';
import moment from 'moment';
import style from './style.module.scss';
import { Tag } from '../../../components/tag';

const { Meta } = Card;

const MINIMAL_VERSION_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const MINIMAL_VERSION_RULES = [
    {
        pattern: MINIMAL_VERSION_PATTERN,
        message: 'Use the x.y.z format, e.g. 7.5.0',
    },
];

interface Props {
    featureFlags: FeatureFlags[] | null;
    isLoading: boolean;
    onSubmit: (updatedFeatureFlag: FeatureFlags) => void;
}

export const FeatureFlagsCard = ({
    featureFlags,
    onSubmit,
    isLoading,
}: Props) => {
    const [editingFeatureFlag, setEditingFeatureFlag] = useState<
        FeatureFlags | undefined
    >(undefined);
    const [form] = Form.useForm();

    const handleEdit = (featureFlag: FeatureFlags) => {
        setEditingFeatureFlag(featureFlag);
        form.setFieldsValue(featureFlag);
    };

    const handleCancel = () => {
        setEditingFeatureFlag(undefined);
        form.resetFields();
    };

    const [featureFlagsData, setFeatureFlagsData] = useState<
        FeatureFlags[] | null
    >(featureFlags);

    useEffect(() => {
        setFeatureFlagsData(featureFlags);
    }, [featureFlags]);

    const handleSubmit = () => {
        form.validateFields()
            .then((values) => {
                const {
                    active,
                    iosMinimalVersion,
                    androidMinimalVersion,
                    activeForTesters,
                } = values;
                const updatedFeatureFlag = {
                    ...editingFeatureFlag!,
                    active,
                    iosMinimalVersion,
                    androidMinimalVersion,
                    activeForTesters,
                };
                onSubmit(updatedFeatureFlag);
                setEditingFeatureFlag(undefined);
                form.resetFields();
                const updatedFeatureFlags = featureFlags?.map((flag) =>
                    flag.id === updatedFeatureFlag.id
                        ? updatedFeatureFlag
                        : flag
                );
                setFeatureFlagsData(updatedFeatureFlags || null);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'iOS Min Version',
            dataIndex: 'iosMinimalVersion',
            key: 'iosMinimalVersion',
        },
        {
            title: 'Android Min Version',
            dataIndex: 'androidMinimalVersion',
            key: 'androidMinimalVersion',
        },
        {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            render: (_: unknown, record: FeatureFlags) => (
                <Tag variant={record.active ? 'success' : 'error'}>
                    {record.active ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Active for Testers',
            dataIndex: 'activeForTesters',
            key: 'activeForTesters',
            render: (_: unknown, record: FeatureFlags) => {
                if (
                    record.activeForTesters === null ||
                    record.activeForTesters === undefined
                ) {
                    return <Tag variant="grey">Inherit</Tag>;
                }
                return (
                    <Tag
                        variant={record.activeForTesters ? 'success' : 'error'}
                    >
                        {record.activeForTesters ? 'Active' : 'Inactive'}
                    </Tag>
                );
            },
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (_: unknown, record: FeatureFlags) => (
                <span>{moment(record.createdAt).format('LLL')}</span>
            ),
        },
    ];

    return (
        <Card bordered={false} loading={isLoading} className={style.card}>
            <Meta title="Feature Flags" />
            <Table
                className="clickable_table_body_rows"
                dataSource={featureFlagsData ?? []}
                columns={columns}
                pagination={false}
                onRow={(row) => ({
                    onClick: () => handleEdit(row),
                })}
            />

            <Modal
                title={`Edit ${editingFeatureFlag?.name}`}
                open={!!editingFeatureFlag}
                onCancel={handleCancel}
                onOk={handleSubmit}
                okText="Update"
            >
                <Form form={form}>
                    <Form.Item
                        label="Active"
                        name="active"
                        initialValue={editingFeatureFlag?.active}
                        valuePropName="checked"
                        rules={[
                            {
                                required: true,
                                message: 'Please select a value for active',
                            },
                        ]}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item
                        label="Active for Testers"
                        name="activeForTesters"
                        initialValue={
                            editingFeatureFlag?.activeForTesters ?? false
                        }
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item
                        label="iOS Minimal Version"
                        name="iosMinimalVersion"
                        initialValue={editingFeatureFlag?.iosMinimalVersion}
                        rules={MINIMAL_VERSION_RULES}
                    >
                        <Input placeholder="Enter iOS minimal version" />
                    </Form.Item>
                    <Form.Item
                        label="Android Minimal Version"
                        name="androidMinimalVersion"
                        initialValue={editingFeatureFlag?.androidMinimalVersion}
                        rules={MINIMAL_VERSION_RULES}
                    >
                        <Input placeholder="Enter Android minimal version" />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};
